/*
 * -----------------------------------------------------------------------
 *  <copyright file="forgot-password.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import React, { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';

import { useBoolean, useTranslation } from 'hooks';
import Settings from 'settings/settings';
import NotificationService from 'utils/notification';
import Button from 'views/components/core/buttons';
import CharactersRemaining from 'views/components/core/characters-remaining';
import { DialogActions } from 'views/components/core/dialog';
import DialogInformationBox from 'views/components/core/dialog-information-box/dialog-information-box';
import FormControl from 'views/components/core/form-control';
import Loading from 'views/components/core/loading';
import TextField from 'views/components/core/text-field';

import './uam-styles.scss';
import { AllRoutes } from '@utils/router/all-routes';

const emailReg = /^[\W]*([\w+\-.%]+@[\w\-.]+\.[A-Za-z]{2,4}[\W]*;{1}[\W]*)*([\w+\-.%]+@[\w\-.]+\.[A-Za-z]{2,4})[\W]*$/;

const ForgotPassword = () => {
    const [email, setEmail] = useState('');
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { orgCode } = useParams();
    const loading = useBoolean(false);
    const disabledSaveButton = useBoolean(true);
    const [error, setError] = useState('');
    const [errorNotification, setErrorNotification] = useState('');

    const validation = (isRequired?: boolean) => {
        let isValid = true;
        let emailError = '';
        if (!email.length && isRequired) {
            emailError = t('ra:requiredFieldError');
            isValid = false;
        } else if (!emailReg.test(email)) {
            emailError = t('uam:incorrectEmailFormat');
            isValid = false;
        }
        setError(emailError);
        return isValid;
    };

    const doSend = async (e: any) => {
        e.preventDefault();
        const isValid = validation(true);

        if (isValid) {
            loading.setTrue();

            fetch(`${Settings.api.url()}/api/forgotpassword`, {
                method: 'POST',
                body: JSON.stringify({ Email: email, OrganisationCode: orgCode }),
                headers: {
                    'Content-Type': 'application/json',
                },
            })
                .then((result: any) => {
                    loading.setFalse();
                    if (result.status === 200) {
                        NotificationService.showNotification(t('login:passwordResetEmailSentToYou'));
                        disabledSaveButton.setTrue();
                        // Take them back to their login page
                        const loginUrl = orgCode
                            ? AllRoutes.path('login-organisation', { orgCode })
                            : AllRoutes.path('login');
                        navigate(loginUrl);
                    } else {
                        setErrorNotification('login:unableToSendPasswordResetEmail');
                        NotificationService.showNotification(t('login:unableToSendPasswordResetEmail'));
                    }
                })
                .catch(() => {
                    loading.setFalse();
                    setErrorNotification('login:unableToSendPasswordResetEmail');
                    NotificationService.showNotification(t('login:unableToSendPasswordResetEmail'));
                });
        }
    };

    const onChange = (e: any) => {
        setErrorNotification('');
        setError('');
        disabledSaveButton.setFalse();
        setEmail(e.target.value);
    };

    return (
        <div className="login-page forgot-password">
            {!loading.value && (
                <>
                    <h1 className="login-title">{t('login:forgottenPassword')}</h1>

                    <DialogInformationBox topText={t('login:forgotPasswordText')} />

                    <form onSubmit={doSend}>
                        <FormControl fullWidth required style={{ marginBottom: '25px' }}>
                            <TextField
                                disabled={loading.value}
                                label={`${t('email')}*`}
                                id="email"
                                type="email"
                                rows={1}
                                autoFocus
                                fullWidth
                                onChange={onChange}
                                placeholder={t('enterYourEmail')}
                                variant="outlined"
                                color="secondary"
                                autoComplete="email"
                                error={error}
                                helperText={
                                    errorNotification ? (
                                        <p style={{ color: '#ae0015' }} className="remaining-text">
                                            {t(errorNotification)}
                                        </p>
                                    ) : (
                                        <CharactersRemaining id="forgot-password-email-help-text" error={error} />
                                    )
                                }
                            />
                        </FormControl>

                        <DialogActions
                            style={{ paddingTop: 20, paddingBottom: 10 }}
                            className="actions-info-wrapper-login-page flex-end"
                        >
                            <Link
                                id="login-forgot-password"
                                to={
                                    orgCode
                                        ? AllRoutes.path('login-organisation', { orgCode })
                                        : AllRoutes.path('login')
                                }
                            >
                                {t('login:returnToLogin')}
                            </Link>

                            <Button
                                disabled={loading.value || disabledSaveButton.value || !!error}
                                text="login:resetPassword"
                                id="reset-password-button"
                                className="login-submit-button"
                                onClick={doSend}
                            />
                        </DialogActions>
                    </form>
                </>
            )}
            {loading.value && <Loading />}
        </div>
    );
};

export default ForgotPassword;
